import React from 'react'
import { useUltraPlayStore } from '@stores'
import {
    StopAutoplayClickedMessage,
    SetSpeedButtonLevelMessage,
    BuyBonusRejectedMessage,
    GameUIMessageType,
    ChangeOpacityComponents,
    ChangeOpacityMessagePrefix,
} from 'game-ui-types'
import UltraPlayControls from './UltraPlayControls'
import UltraPlayFeatureDialog from './UltraPlayFeatureDialog'
import UltraPlayInsufficientBalanceModal from './UltraPlayInsufficientBalanceModal'
import { UltraPlaySoundEffects } from './useUltraPlaySoundEffects'

const HIDDEN_COMPONENTS = [
    ChangeOpacityComponents.BET_SLIDER,
    ChangeOpacityComponents.JACKPOT_SECTION,
    ChangeOpacityComponents.PROMO_WIDGET,
    ChangeOpacityComponents.INFO_LINE,
    ChangeOpacityComponents.REMAINING_SPINS_BANNER,
    ChangeOpacityComponents.NAVIGATION,
    ChangeOpacityComponents.PLAYER_INFO,
    ChangeOpacityComponents.BUY_BONUS_BUTTON,
]

const setComponentsVisibility = (visible: boolean) => {
    const payload = visible ? { opacity: 1, isInteractive: true } : { opacity: 0, isInteractive: false }

    HIDDEN_COMPONENTS.forEach((component) => {
        window.postMessage({ type: `${ChangeOpacityMessagePrefix}${component}`, payload }, '*')
    })

    // TODO: remove when promo removed from platform
    window.parent.postMessage({ type: GameUIMessageType.CHANGE_OPACITY_PROMO_WIDGET, payload }, '*')
}

const setSpeedLevel = (speedLevel: number) => {
    window.postMessage(
        { type: GameUIMessageType.SET_SPEED_BUTTON_LEVEL, speedLevel } as SetSpeedButtonLevelMessage,
        '*'
    )
}

const UltraPlayModal: React.FC = () => {
    const isOpen = useUltraPlayStore((s) => s.isOpen)
    const showFeatureChoiceDialog = useUltraPlayStore((s) => s.showFeatureChoiceDialog)
    const showInsufficientBalance = useUltraPlayStore((s) => s.showInsufficientBalance)

    const [complianceHeight, setComplianceHeight] = React.useState(0)

    React.useEffect(() => {
        if (!isOpen) return

        const el = document.querySelector('[data-compliance-bar]') as HTMLElement | null

        if (!el) return

        const obs = new ResizeObserver(() => setComplianceHeight(el.offsetHeight))

        obs.observe(el)
        setComplianceHeight(el.offsetHeight)

        return () => obs.disconnect()
    }, [isOpen])

    React.useEffect(() => {
        setComponentsVisibility(!isOpen)
    }, [isOpen])

    const handleClose = () => {
        const { status, stop, reset, close, previousSpeedLevel, selectedBuyBonusFeatureName } =
            useUltraPlayStore.getState()
        const isActive = status === 'PLAYING' || status === 'PAUSED'

        if (isActive) {
            window.postMessage({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage, '*')
            stop()
        }

        if (selectedBuyBonusFeatureName !== 'OFF') {
            window.postMessage({ type: GameUIMessageType.BUY_BONUS_REJECTED } as BuyBonusRejectedMessage, '*')
        }

        reset()

        // Restore the speed the player had before Ultra Play.
        setSpeedLevel(previousSpeedLevel)
        close()
    }

    if (!isOpen) return null

    return (
        <div
            className="fixed left-0 right-0 top-0 z-[9998] overflow-hidden text-white bg-ultra-surface"
            style={{ bottom: complianceHeight }}
        >
            <UltraPlaySoundEffects />
            {showFeatureChoiceDialog && <UltraPlayFeatureDialog onClose={handleClose} />}
            {showInsufficientBalance && <UltraPlayInsufficientBalanceModal />}
            <UltraPlayControls onClose={handleClose} />
        </div>
    )
}

export default UltraPlayModal
